/**
 * Where to send someone after they sign in.
 *
 * RequireAuth puts the location they were trying to reach into router state
 * (`{ from: location }`) when it bounces them to /login. Without this, every
 * deep link died at the sign-in screen: a teammate clicking "Open ticket" in the
 * Slack support channel while signed out would sign in and land on the portal
 * home, with the ticket they came for gone.
 *
 * Router state, not a `?next=` query parameter, on purpose: state can only be
 * set by the app's own navigation, so an outside link cannot aim the post-login
 * redirect anywhere. It is still validated — it must be an in-app path — because
 * this is the one place a redirect target is chosen, and "anything that isn't
 * clearly ours goes home" is cheap to hold.
 */
export function returnPathFrom(state: unknown): string {
  const from = (state as { from?: { pathname?: unknown; search?: unknown; hash?: unknown } } | null)?.from;
  const pathname = typeof from?.pathname === 'string' ? from.pathname : '';

  // In-app absolute paths only. `//host` is protocol-relative — another origin.
  if (!pathname.startsWith('/') || pathname.startsWith('//') || pathname.startsWith('/\\')) return '/';
  // Never back to the sign-in screen itself.
  if (pathname === '/login') return '/';

  const search = typeof from?.search === 'string' ? from.search : '';
  const hash = typeof from?.hash === 'string' ? from.hash : '';
  return pathname + search + hash;
}
